import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Stack,
  Typography,
  StepConnector,
} from "@mui/material";

import "./VoucherForm.scss";
import ApplicantDetail from "./components/ApplicantDetail";
import ReviewYourDetails from "./components/ReviewYourDetails";
import {
  COMPARE_VALUE,
  ERROR_MESSAGE,
  VOUCHER_DYNAMIC_STEPS,
  VOUCHER_INITIAL_STEPS,
  FORM_PHASE,
  EOIFormStatus,
} from "../../config/constant";
import OtherApplicantDetails from "./components/OtherApplicantDetail";
import { useParams, useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  getMasterDataList,
  getVoucherById,
  setActiveStep,
  setApplicantActiveStep,
  setApplicantTotal,
  setEditPreviewApplicant,
  setResetData,
  setShowMobileStepper,
  setApplicantActiveStep1,
  setStepsCompleted,
  getEOICampaignDetails
} from "./voucher-form.slice";
import OtherDetailStepIcon from "../../assets/component-icon/OtherDetailStepIcon";
import ReviewDetailStepIcon from "../../assets/component-icon/ReviewDetailStepIcon";
import PaymentDetailStepIcon from "../../assets/component-icon/PaymentDetailStepIcon";
import ApplicantDetailStepIcon from "../../assets/component-icon/ApplicantDetailStepIcon";
import Header from "../../layout/header/Header";
import SnackbarAlert from "../../shared-components/snackbar-alert/SnackbarAlert";
import PaymentOptions from './components/PaymentOptions';

import Arrowgreen from "../../assets/icons/line2.svg";
import EoiUnitPreference from "./components/EOIUnitPreference";
import VoucherUnitPreference from "./components/VoucherUnitPreference";
import Loader from "../../components/Loader";
import StepperIconRoot from "../../shared-components/stepper/StepperIconRoot";
import { useVoucherFormMode } from "../../utilis/use-voucher-form-mode";

const VoucherForm = () => {
  const dispatch = useDispatch();
  const {
    activeStep,
    applicantTotal,
    applicantData,
    showMobileStepper,
    applicantActiveStep,
    checkFirstStepCompleted,
    applicantActiveStep1,
    applicantActiveStep2,
    applicantEditPreview,
    isUpgradePreferentialStep,
    isLoading,
  } = useSelector((state) => state.voucherForm);
  const { voucherId } = useParams();
  const history = useHistory();
  const { isApplicantUpdateMode } = useVoucherFormMode();
  const [steps, setSteps] = useState([]);
  const [stepsArray, setStepsArray] = useState([]);
  const [lastStep, setLastStep] = useState(0);
  const [snackbarConfig, setSnackbarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const ALLOWED_STATUSES = new Set([
    EOIFormStatus.UNVERIFIED,
    EOIFormStatus.MIS_VERIFIED,
    EOIFormStatus.MIS_REQUESTED_CHANGES,
    EOIFormStatus.MIS_UPDATED,
    EOIFormStatus.CRM_VERIFIED,
    EOIFormStatus.CRM_REQUESTED_CHANGES,
    EOIFormStatus.CRM_UPDATED,
    EOIFormStatus.CANCEL_REQUESTED,
    EOIFormStatus.CANCEL_ACCEPTED,
    EOIFormStatus.CANCEL_APPROVED,
    EOIFormStatus.REFUND_INITIATED,
    EOIFormStatus.CANCELLED,
    EOIFormStatus.UPGRADED,
    EOIFormStatus.ACTIVE,
    EOIFormStatus.CONVERTED,
  ]);

  useEffect(() => {
    if (applicantData && applicantData?.voucherId !== voucherId) {
      dispatch(setResetData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherId]);

  useEffect(() => {
    setLastStep(applicantData?.lastStep);
    let newArray = VOUCHER_DYNAMIC_STEPS.slice(0, applicantTotal);
    let initialSteps = VOUCHER_INITIAL_STEPS.map((data, index) => {
      return {
        label: "Step " + (applicantTotal + index + 1),
        description: data.description,
      };
    });

    let applicantDetailArray = newArray.map((data, index) => {
      if (index < 2) {
        return <ApplicantDetail applicantNo={index + 1} />;
      } else {
        return <OtherApplicantDetails applicantNo={index + 1} />;
      }
    });

    if (isApplicantUpdateMode) {
      setSteps(newArray);
      setStepsArray(applicantDetailArray);
      return () => { };
    }

    const FORCE_EOI = true;
    let remainingDetailArray = [
      FORCE_EOI ? <EoiUnitPreference /> : applicantData?.formPhase === FORM_PHASE.VOUCHER && !isUpgradePreferentialStep ? <VoucherUnitPreference /> : <EoiUnitPreference />,
      <ReviewYourDetails />,
      <PaymentOptions />,
    ];

    setSteps([...newArray, ...initialSteps]);
    setStepsArray([...applicantDetailArray, ...remainingDetailArray]);

    return () => { };
  }, [applicantTotal, activeStep, applicantData, isApplicantUpdateMode]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      if (voucherId?.length > 0) {
        dispatch(getVoucherById(`/${voucherId}`))
          .unwrap()
          .then((res) => {
            // Set applicantTotal from API response
            dispatch(setApplicantTotal(res?.noOfApplicants || 1)); // fallback to 1 if not present
            dispatch(getMasterDataList());
            dispatch(getEOICampaignDetails({ campaignId: res?.campaignId || "" }));


            if ((ALLOWED_STATUSES.has(res?.voucherFormStatus) || res?.toBeVerified) && !applicantEditPreview && !isApplicantUpdateMode) {
              dispatch(setActiveStep(0));
              history.push(`/voucher-form/thank-you/${voucherId}`);
            }
          })
          .catch((error) => {
            dispatch(setResetData());
            if (!error?.errors?.message?.includes("EOI/Voucher Form with ID")) {
              setSnackbarConfig({
                open: true,
                message: ERROR_MESSAGE,
                severity: "error",
              });
            }
            history.push('/404')
          });
      }
    }, 300);

    return () => clearTimeout(debounceTimeout);

  }, [dispatch, history, voucherId, isApplicantUpdateMode]);






  function ColorlibStepIcon(props) {
    const { active, className, icon } = props;
    const isStepCompleted =
      applicantData?.applicant1?.professionalDetails && checkFirstStepCompleted === false && lastStep >= icon;
    const icons = {};
    const isStepActiveAndCompleted = isStepCompleted && active;
    const nextStepIcons = {
      1: (
        <OtherDetailStepIcon
          show={Boolean(active)}
          completed={isStepCompleted}
        />
      ),
      // 2: (
      //   <OtherDetailStepIcon
      //     show={Boolean(active)}
      //     completed={isStepCompleted}
      //   />
      // ),
      2: (
        <ReviewDetailStepIcon
          // show={Boolean(active)}
          completed={isStepCompleted}
        />
      ),
      3: (
        <PaymentDetailStepIcon
          show={Boolean(active)}
          completed={isStepCompleted}
        />
      ),

    };

    let applicantDetailSteps = VOUCHER_DYNAMIC_STEPS.slice(0, applicantTotal);

    for (let index = 0; index < applicantDetailSteps.length; index++) {
      icons[index] = (
        <ApplicantDetailStepIcon show={active} completed={isStepCompleted} />
      );
    }

    const maxIndex = Math.max(...Object.keys(icons).map(Number));

    Object.keys(nextStepIcons).forEach((key, index) => {
      icons[maxIndex + index + 1] = nextStepIcons[key];
    });

    return (
      <StepperIconRoot
        ownerState={{
          active,
          isStepCompleted,
          variant: "default",
        }}
        className={className}
      >
        {icons[props.icon]}
        <div className="step-label">
          {isStepActiveAndCompleted && (
            <img
              src={Arrowgreen}
              alt="Completed Step"
              className="arrowTriangleLeft"
            />
          )}
        </div>
      </StepperIconRoot>
    );
  }

  const getForm = (step) => {
    return stepsArray[step];
  };

  const handleSnackbarClose = () => {
    setSnackbarConfig({
      open: false,
      message: "",
      severity: "",
    });
  };

  const handleStep = (step) => () => {
    dispatch(setShowMobileStepper(false));
    if (lastStep >= step) {

      dispatch(setEditPreviewApplicant(false));

      dispatch(setActiveStep(step));
      if (step + 1 <= applicantData?.noOfApplicants && step + 1 !== 3 && step + 1 !== 4) {
        if (step + 1 === 1) {
          dispatch(setApplicantActiveStep(applicantActiveStep1));
        }
        if (step + 1 === 2) {
          dispatch(setApplicantActiveStep(applicantActiveStep2));
        }
      }
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [applicantActiveStep, activeStep])

  useEffect(() => {
    if (activeStep + 1 <= applicantData?.noOfApplicants && activeStep + 1 !== 3 && activeStep + 1 !== 4) {
      if (activeStep + 1 === 1) {
        dispatch(setApplicantActiveStep(applicantActiveStep1));
      }
      if (activeStep + 1 === 2) {
        dispatch(setApplicantActiveStep(applicantActiveStep2));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep])


  useEffect(() => {
    if (applicantData?.voucherFormStatus === EOIFormStatus?.CREATED) {
      dispatch(setActiveStep(0));
      dispatch(setApplicantActiveStep(0))
      dispatch(setApplicantActiveStep1(0));
      dispatch(setStepsCompleted(0));

    }
    if (applicantData?.voucherFormStatus !== EOIFormStatus?.UNVERIFIED) {
      if (isApplicantUpdateMode) {
        dispatch(setActiveStep(0));
        dispatch(setApplicantActiveStep(0));
        dispatch(setApplicantActiveStep1(0));
      } else {
        dispatch(setActiveStep(applicantData?.lastStep || 0));
      }
    }
    if (isUpgradePreferentialStep && applicantData?.voucherFormStatus === EOIFormStatus?.UNVERIFIED) {
      dispatch(setActiveStep(applicantData?.noOfApplicants))
      //dispatch(setisUpgradePreferentialStep(false))
    }

  }, [])


  useEffect(() => {
    if (applicantData?.voucherFormStatus === EOIFormStatus.CREATED) {
      dispatch(setActiveStep(0));
      dispatch(setApplicantActiveStep(0))
      dispatch(setApplicantActiveStep1(0));
      dispatch(setStepsCompleted(0));
    }

  }, [applicantData?.voucherFormStatus])

  useEffect(() => {
    if (applicantData?.voucherFormStatus === EOIFormStatus.IN_PROGRESS) {
      if (isApplicantUpdateMode) {
        dispatch(setActiveStep(0));
        dispatch(setApplicantActiveStep(0));
        dispatch(setApplicantActiveStep1(0));
      } else {
        dispatch(setActiveStep(applicantData?.lastStep || 0));
      }
    }
  }, [applicantData?.lastStep, isApplicantUpdateMode])

  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      <Helmet>
        <title>{`${applicantData?.formPhase === FORM_PHASE.VOUCHER && !isUpgradePreferentialStep ? "Voucher Form" : "Expression of Interest Form"}${isApplicantUpdateMode ? " - Update Applicant Info" : ""} - Puravankara`}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Header
        logo={COMPARE_VALUE?.Puravankara}
        isMenuOpen={showMobileStepper}
        onToggleMenu={() => dispatch(setShowMobileStepper(!showMobileStepper))}
      />
      <Stack>
        <Grid
          container
          spacing={0}
          sx={{
            p: 0,
            paddingLeft: {
              xs: "16px",
              sm: "16px",
              md: "16px",
              lg: "7%",
            },
            paddingRight: {
              xs: "16px",
              sm: "16px",
              md: "16px",
              lg: "7%",
            },
            mb: {
              xs: 2,
              sm: 4,
              md: 5, // Desktop
            },
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              textAlign: {
                xs: "center",
                sm: "center",
                md: "center",
              },
              pt: {
                xs: "16px",
                sm: "30px",
                md: "36px",
              },
              pb: {
                xs: "16px",
                sm: "18px",
                md: "20px",
              },
            }}
          >
            <Typography
              sx={{
                color: "#1A407D",
                fontSize: {
                  xs: "20px",
                  sm: "20px",
                  md: "24px",
                },
                fontWeight: "700",
                lineHeight: {
                  xs: "28px",
                  sm: "32px",
                  md: "36px",
                },
                mb: {
                  xs: 0.5,
                  sm: 1,
                  md: 1,
                },
              }}
            >
              {applicantData?.formPhase === FORM_PHASE.VOUCHER && !isUpgradePreferentialStep ? "Voucher Form" : "Expression of Interest Form"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Box
              className={`mobileDrawer ${showMobileStepper === true ? "OpenDrawer" : ""
                }`}
              sx={{
                borderRadius: "8px",
                border: "1px solid rgba(26, 64, 125, 0.30)",

              }}
            >
              <div className="mobileDrawerOverly"> </div>
              <div className="mobileDrawerBox">
                <Stepper
                  activeStep={activeStep}
                  orientation="vertical"
                  connector={
                    <StepConnector
                      sx={{
                        ml: "19px",
                      }}
                    />
                  }
                  sx={{
                    "& .MuiStepConnector-line": {
                      border: "0px",
                      borderLeft: "2px dotted #D6D6D6",
                      minHeight: "34px",
                    },
                    "& .MuiStepLabel-label": {
                      color: activeStep === 0 ? "green" : "blue",
                    },
                    "& .MuiStepIcon-root": {
                      color: activeStep === 0 ? "orange" : "purple",
                    },
                  }}
                >
                  {steps.map((step, index) => {
                    return (
                      <Step key={step?.description || index}>
                        <StepLabel
                          StepIconComponent={(props) => (
                            <ColorlibStepIcon {...props} icon={index} />
                          )}
                          sx={{
                            color: "#7A7A7A",
                            fontSize: "12px",
                            fontWeight: "400",
                            lineHeight: "18px",
                            mb: "3px",
                            cursor: "pointer",
                          }}
                          onClick={handleStep(index)}

                        >
                          <Typography
                            sx={{
                              color: "#7A7A7A",
                              fontSize: "12px",
                              fontWeight: "400",
                              lineHeight: "18px",
                              mb: "3px",
                            }}
                          >
                            {step.label}
                          </Typography>
                          <Typography
                            sx={{
                              color: "#1C252E",
                              fontSize: "14px",
                              fontWeight: "600",
                              lineHeight: "22px",
                            }}
                            dangerouslySetInnerHTML={{
                              __html: step.description,
                            }}
                          ></Typography>
                        </StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>
              </div>
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={9}>
            <Box
              sx={{
                borderRadius: "8px",
                border: "1px solid rgba(26, 64, 125, 0.30)",
                padding: {
                  xs: "16px",
                  sm: "16px",
                  lg: "26px 39px 26px 39px",
                },
                marginLeft: {
                  xs: "0",
                  sm: "0",
                  lg: "24px",
                },
              }}
            >
              {getForm(activeStep)}
            </Box>
          </Grid>
        </Grid>
      </Stack>
      <SnackbarAlert
        open={snackbarConfig.open}
        message={snackbarConfig.message}
        severity={snackbarConfig.severity}
        onClose={handleSnackbarClose}
      />
    </>
  );
};



VoucherForm.propTypes = {
  active: PropTypes.any,
  className: PropTypes.any,
  icon: PropTypes.any,
};

export default VoucherForm;